import styled from "styled-components";
import {SiteLink as RouterLink} from "nav/SiteLink";
import {fontFamilies, radii} from "theme/tokens";
import {eyebrowTextCss} from "theme/typography";

/**
 * Externalized styles for {@link ./AdvertisingTreatments} (ui#1861) — the treatment gallery, in the two
 * labelled category rows kitecorp/transferify-ui#1870 split it into.
 *
 * The preview STAGE is not defined here. It is `BackdropTemplatePicker.styles.PreviewStage`, imported by
 * the component, because these cards show the same thing the booking page's picker shows and a second
 * stage with its own aspect ratio and clip would drift from it on the first change to either.
 */

/**
 * One labelled category and its grid.
 *
 * The rows are stacked rather than run together because they are different things to buy: a backdrop
 * animates behind the download card and a wallpaper takes the whole page, which is the distinction the
 * booking form is built on and the reason `categoryGroups` exists at all. Ten tiles in one run would
 * erase it.
 */
export const TreatmentCategory = styled.div`
	margin-top: 36px;
`;

/** The category's own name, from `sponsor.categories` — the booking form's heading, not a second one. */
export const TreatmentCategoryName = styled.h3`
	margin: 0;
	font-size: 17px;
	font-weight: 700;
	letter-spacing: -0.015em;
	color: ${({theme}) => theme.tokens.ink};
`;

/** Its one line: what this category does to the page. */
export const TreatmentCategoryLede = styled.p`
	margin: 5px 0 0;
	max-width: 62ch;
	font-size: 13.5px;
	line-height: 1.55;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const TreatmentGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 20px;
	margin-top: 18px;

	@media (max-width: 900px) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	@media (max-width: 520px) {
		grid-template-columns: 1fr;
	}
`;

/**
 * One card: a real anchor at the booking flow.
 *
 * A LINK rather than the picker's radio, and that difference is the point of this band. Nothing is being
 * selected here — a reader who likes a look should end up on the page where they can actually choose it,
 * so every card goes to the same place the page's buttons go.
 */
export const TreatmentCard = styled(RouterLink)`
	&& {
		display: block;
		overflow: hidden;
		border-radius: ${radii.card};
		border: 1px solid ${({theme}) => theme.tokens.cardBorder};
		background: ${({theme}) => theme.tokens.card};
		color: inherit;
		text-decoration: none;
		transition: border-color 0.16s, box-shadow 0.16s;

		&:hover {
			border-color: ${({theme}) => theme.tokens.blue};
			box-shadow: ${({theme}) => theme.tokens.shadowCard};
		}

		&:focus-visible {
			outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
			outline-offset: 2px;
		}
	}
`;

/**
 * The stage's frame inside the card.
 *
 * `PreviewStage` carries its own rounded corners for a card that pads around it; here it sits flush
 * against the card's top edge, so this wrapper squares the bottom two and lets the card's own radius
 * clip the top two.
 */
export const TreatmentStageFrame = styled.div`
	position: relative;

	& > * {
		border-radius: 0;
		border-left: 0;
		border-right: 0;
		border-top: 0;
	}
`;

export const TreatmentBody = styled.div`
	padding: 14px 16px 16px;
`;

/**
 * The treatment's name and its image arity on one baseline.
 *
 * An `<h4>` since kitecorp/transferify-ui#1870: the category heading above it took `<h3>`, and a card
 * that stayed at the same level as the group containing it would tell a screen reader the two are
 * siblings.
 */
export const TreatmentName = styled.h4`
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 10px;
	margin: 0;
	font-size: 16px;
	font-weight: 700;
	line-height: 1.3;
	color: ${({theme}) => theme.tokens.ink};
`;

/** "2-6 images", from the booking page's own `assetHint`. Mono, so it reads as a quantity. */
export const TreatmentArity = styled.span`
	${eyebrowTextCss};
	flex: none;
	font-size: 10.5px;
	text-transform: none;
	white-space: nowrap;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const TreatmentDescription = styled.p`
	margin: 5px 0 0;
	font-size: 13.5px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * The placeholder the stage holds until the previews mount.
 *
 * Sized by the SAME `aspect-ratio: 16 / 10` the real stage uses, so every card occupies its final
 * height in the pre-rendered HTML and nothing below it moves when the art arrives. It is deliberately
 * empty: the static page must not describe a live preview it does not contain.
 */
export const TreatmentStagePlaceholder = styled.div`
	aspect-ratio: 16 / 10;
	background: ${({theme}) => theme.tokens.field};
	border-bottom: 1px solid ${({theme}) => theme.tokens.line};
`;

/** The "Live preview" caption, floated over the stage's corner — the picker's own badge, restated
    here because the picker's version is positioned against a padded card and this one is flush. */
export const TreatmentBadge = styled.span`
	${eyebrowTextCss};
	position: absolute;
	z-index: 2;
	left: 10px;
	bottom: 10px;
	padding: 3px 8px;
	border-radius: ${radii.chip};
	background: ${({theme}) => theme.tokens.card};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	font-family: ${fontFamilies.mono};
	font-size: 9.5px;
	color: ${({theme}) => theme.tokens.ink2};
`;
